// The one error every `DatabaseWrapper` read — and the single-document write path — throws
// when it could not run at all.
//
// Kept in its own Angular-free, pouch-free module so it can be imported from a
// unit test and from pure helpers without dragging in `database-wrapper.ts`
// (which transitively imports `pouchdb-browser`, references `self`, and cannot
// load in the node test runner) — same reason `food-read-error.ts` is separate.

/**
 * Thrown when a `DatabaseWrapper` read could not run because the local database
 * was not available: the recreation gate reported shutdown, the read lock could
 * not be taken (the shutdown flag is one-way and permanent — see
 * `shared/src/read-write-lock.ts`), or there was no database handle to read from.
 *
 * **Since df-6ce1f9d6830e, `saveRecordAsync` and `deleteRecordAsync` throw it too**, at those
 * same three gates. They used to write a `console.log` line and return, so a caller could not
 * tell "the document was written" from "the write was refused" — and every caller read the
 * quiet return as success. `design-docs/pass-through-db.md` has the worked example and the
 * list of callers that deliberately stay quiet. The bulk and raw write paths still answer the
 * old way and are recorded as their own deferred finding.
 *
 * **`deleteRecordAsync` throws `Recovering` on a fourth occasion (df-068808a2796e)**, which is
 * not one of those gates: the write STARTED, `db.put` failed, and the database recovery took
 * the failure over — so the save ladder returned normally having written nothing. A delete
 * cannot treat that as done, because a deleted object is never saved again and its `_deleted`
 * mark would be left standing on the shared object. An ordinary save still treats it as
 * handled and throws nothing. Same doc for the full story.
 *
 * **This is emphatically not "there are no matching documents".** That
 * distinction is the whole reason this class exists. Before it, the wrapper gave
 * three different answers to this one condition — `getRecordAsync` and friends
 * threw a bare `Error`, `getByIdHeadAsync` returned `[]`, and `getByIdRangeAsync`
 * and `runQueryAsync` returned `null`. The two that answered with an empty result
 * were routinely read as a real answer: a suppressed view read told the caller
 * "this split day has never been completed", which is how an onboarding card got
 * shown to an established user (deferred finding #2). Nothing threw, so the
 * `try/catch` the callers had already written around it could never fire, and no
 * telemetry recorded any of it.
 *
 * `strictNullChecks` is off for the client, so a `T[] | null` return is invisible
 * to the compiler and a forgotten `null` check costs nothing at build time. A
 * thrown error is the only signal here that a caller cannot ignore by accident.
 *
 * **Catching it is fine — silently swallowing it is not.** A caller that must
 * keep going (a page being torn down, a cache that must not persist a wrong
 * value) should catch this type specifically, keep its conservative default, and
 * say so — either by reporting via `LoggingService.trackSilentProblem` or, where
 * the condition is genuinely expected and harmless, with a written comment
 * saying why. Catching `Error` broadly, or matching on the message text, is what
 * this type exists to replace.
 *
 * **What happens to one that nobody catches.** `app-sentry.ts` recognises this
 * type in both nets (the `unhandledrejection` listener and `SentryErrorHandler`)
 * and splits it three ways (df-0c444ca6bb2a):
 *
 *  - during the sign-out wipe — once `isIntentionalDestruction()` is true — it is
 *    `console.log` only, and no Sentry event at all;
 *  - when a shutdown has begun but that flag is not up yet, which is the ordinary
 *    sign-out window, it goes through
 *    `trackSilentInfo('DatabaseUnavailableDuringTeardown')`: Sentry keeps the
 *    record at info level, and the console line is a `console.log`, so it cannot
 *    trip the E2E unexpected-browser-error gate;
 *  - otherwise — a transient recovery, or no database handle at all — it goes
 *    through `trackSilentProblem('DatabaseUnavailableUnhandled')`, so Sentry gets
 *    it as a problem with its reason. Note `trackSilentProblem` DOES emit its own
 *    `console.error` line, so an E2E scenario that can hit THAT window needs
 *    `DatabaseUnavailableUnhandled` in its ignore list.
 *
 * There is no user-facing error toast in any of the three.
 *
 * That handling had to be added with this type; do not assume the pre-existing
 * `isIntentionalDestruction()` suppression covers it. That guard is `&&`-ed with
 * `isIdbError`, which matches only `indexed_db_went_bad` and a fixed list of IDB
 * message substrings — a `DatabaseUnavailableError` is neither, so it fell
 * straight through to a toast. The two realistic escape routes are the sign-out
 * wipe (expected — we are destroying the databases on purpose) and a transient
 * IDB recovery on iOS backgrounding, which resolves on its own and which the
 * user can do nothing about. Neither deserves a toast; both deserve a record.
 */
export class DatabaseUnavailableError extends Error {
  /** Which database wrapper refused — its `internalDatabaseIdentifier`. */
  readonly databaseIdentifier: string;
  /** Which check refused, for telemetry and for debugging. */
  readonly reason: DatabaseUnavailableReason;

  /**
   * `detail` names the specific thing that could not be read — today, which singleton.
   * It goes in the MESSAGE only, never into `databaseIdentifier`, because that field
   * answers one question — WHICH WRAPPER refused — and its value is meant to be one of
   * the small fixed set of wrapper names. `app-sentry.ts` sends it on as the `database`
   * property of the report, where a reader filtering by it expects exactly that. Folding
   * a per-document value in makes the field answer two questions at once and stop being
   * usable for either. (It does not affect how Sentry GROUPS the reports — those group
   * on the message — so the cost here is a field that misreports, not lost grouping.)
   */
  /**
   * `callSite` is where the refused call was made from, captured with `captureDatabaseCallSite`
   * (#6205). It becomes the error's `cause`. Read that function's comment for why.
   */
  constructor(databaseIdentifier: string, reason: DatabaseUnavailableReason, detail?: string, callSite?: Error) {
    super(
      `Database not available (${reason}): ${databaseIdentifier}${detail ? ` — ${detail}` : ''}`,
      callSite ? { cause: callSite } : undefined,
    );
    this.name = 'DatabaseUnavailableError';
    this.databaseIdentifier = databaseIdentifier;
    this.reason = reason;
  }
}

/** Why a read could not run. All of these mean the same thing to a caller — the
 *  answer is unknown, not empty — but they are worth telling apart in a log, and
 *  they split in one way that matters: some are TRANSIENT, meaning the wrapper is
 *  alive and the same read may succeed shortly, while the rest mean it is being
 *  torn down and will not answer again. `Recovering` and `SingletonNotLoaded` are
 *  the transient ones; `isDatabaseTornDown` below is where that split is applied,
 *  and `TRANSIENT_REASONS` beside it is the list to add to. */
export enum DatabaseUnavailableReason {
  /** The recreation gate reported that the wrapper is shutting down. */
  ShuttingDown = 'shutting down',
  /** The read lock could not be taken, which only happens after shutdown began. */
  ReadLockRefused = 'read lock refused, shutting down',
  /** There was no database handle to read from. */
  NoDatabaseHandle = 'no database handle',
  /** The read had already started and was intercepted mid-flight by a database
   *  recovery (`DatabaseRecoveryService.interceptDatabaseError`). Unlike the three above
   *  this one is transient — the read is worth trying again once recovery
   *  finishes — but the answer right now is still unknown, not empty, so it
   *  belongs to the same family and the same type.
   *
   *  `deleteRecordAsync` throws it for the WRITE-side version of the same condition
   *  (df-068808a2796e): the put had started and the recovery took its failure over, so
   *  nothing was written and nothing threw. It is the same condition and equally worth
   *  retrying, which is why it is this reason rather than one of its own.
   *
   *  Keep the wording `recovering`: `add-exercise-restores-progression-3321.spec.ts`
   *  ignores `/Database not available \(recovering\)/` for a refresh race, and
   *  that pattern matches this enum value through the message format above. */
  Recovering = 'recovering',
  /** Start-up has not loaded this singleton document yet, so there is nothing to
   *  answer with (#4801).
   *
   *  It belongs in this family for the reason the family exists: the honest answer
   *  is "I could not look", and the alternative — handing back `undefined` — was
   *  read as "this document is empty". That is not a hypothetical. A coach-invite
   *  claim copied a coach's whole training program into an unloaded plan document,
   *  `deepOverwriteCouchSafe` skipped the copy because its destination was missing,
   *  and nothing anywhere said a word about it.
   *
   *  Like `Recovering`, this one is transient — start-up may still be running, and
   *  the same read a moment later can succeed. */
  SingletonNotLoaded = 'singleton not loaded',
  /** A SAVE of a singleton document that start-up could not read at all, from the server
   *  or from this device, so the app holds only an empty default for it (#6248). Writing
   *  that default would overwrite the user's real document, so the save is refused.
   *
   *  On the transient side of the split: the wrapper is alive, and the next start-up reads
   *  the document again. The cold start holds the loading screen in this state and reloads,
   *  so a caller only meets this if it saves behind that held screen, and that should be
   *  reported rather than read as a teardown. */
  SingletonNotReadAtBoot = 'singleton not read at boot',
}

/**
 * Record where a database call is being made from, so a later refusal can name its caller (#6205).
 *
 * The wrapper builds most refusals after an `await`. By then the caller's code is no longer on the
 * JavaScript stack, so the refusal's own stack names only the wrapper. Chrome's engine rebuilds
 * the path through an awaited chain, but Safari's does not. So on iOS a background read that nobody
 * caught was filed in Sentry (CYBERNETIC-FITNESS-APP-4BV) with no way to tell which code started it.
 *
 * Each refusing wrapper method calls this as its FIRST statement, while the caller is still on the
 * stack. It passes the result to every refusal it throws, and the refusal keeps it as `cause`.
 * Nothing else needs wiring for it to show: `describeErrorForLog` follows `cause` into the report's
 * `error` field, and Sentry's default linked-errors integration shows it as a linked exception.
 *
 * Cost: it builds one `Error` per call and never reads `.stack`, so the stack text is formatted
 * only for a refusal that is actually described. Measured in Node, that is about 1.6 microseconds
 * per call.
 */
export function captureDatabaseCallSite(): Error {
  const callSite = new Error('database call made here');
  callSite.name = 'DatabaseCallSite';
  return callSite;
}

/** True when the value is the wrapper's unavailable error.
 *
 *  Use this at every call site instead of matching the message text — the
 *  wording lives in `DatabaseUnavailableReason` and reads like free-form prose,
 *  so a substring match breaks silently the first time someone rewords it.
 *
 *  It is a plain `instanceof`, which is sound because every read that throws one
 *  comes from the single `DatabaseWrapper` module. It would NOT survive a
 *  duplicated module instance (a lazily-loaded chunk with its own copy of the
 *  class). If that ever happens, widen it here — also check `name` — and no
 *  call site has to change. */
export function isDatabaseUnavailable(err: unknown): err is DatabaseUnavailableError {
  return err instanceof DatabaseUnavailableError;
}

/** True when the wrapper refused because it is being TORN DOWN, as opposed to
 *  being transiently unavailable.
 *
 *  The split is the one this enum already documents: `ShuttingDown`,
 *  `ReadLockRefused` (the lock's shutdown flag is one-way and permanent) and
 *  `NoDatabaseHandle` all mean the wrapper is gone or going. The other two are
 *  transient and worth trying again — `Recovering` (the read was intercepted
 *  mid-flight by a database recovery) and `SingletonNotLoaded` (start-up has not
 *  reached that document yet). They are listed in `TRANSIENT_REASONS` below, which
 *  is the one place to extend when a reason is added.
 *
 *  Use this where a caller wants to stay quiet about our own teardown while
 *  still reporting a genuine transient. **`DatabaseRecoveryService.isIntentionalDestruction()`
 *  does NOT cover this window** and is the wrong test for it: the sign-out path
 *  calls `shutdownAsync()` on every wrapper FIRST, and only raises the wipe flag
 *  later, when the login page triggers `reloadAppAsync`. So an in-flight read
 *  that starts after the shutdown throws with that flag still false. Anything
 *  gating on the flag alone reports our own teardown as a fault — including a
 *  `console.error`, which the E2E unexpected-browser-error gate fails on. */
export function isDatabaseTornDown(err: unknown): boolean {
  return isDatabaseUnavailable(err) && !TRANSIENT_REASONS.includes(err.reason);
}

/** The reasons that are NOT a teardown: the wrapper is alive and the same read may
 *  well succeed shortly. Keeping them in one list rather than a chain of `!==` is
 *  what stops the next reason being added on the wrong side by omission — which is
 *  how `SingletonNotLoaded` first landed, silently turning a read that could not run
 *  into a `console.log` at every site that gates on `isDatabaseTornDown`. */
const TRANSIENT_REASONS: readonly DatabaseUnavailableReason[] = [
  DatabaseUnavailableReason.Recovering,
  DatabaseUnavailableReason.SingletonNotLoaded,
  DatabaseUnavailableReason.SingletonNotReadAtBoot,
];

/** True when the wrapper refused because a SHUTDOWN had already begun — and only then.
 *
 *  This is deliberately NARROWER than `isDatabaseTornDown` above, and the difference is
 *  exactly one reason: `NoDatabaseHandle`. That one means "there was nothing to read from",
 *  which early boot produces, before start-up has opened the database at all, and so do a
 *  wrapper re-created after a sign-out, a failed local-store swap, and a purge or restore that
 *  destroyed the store. None of those is a shutdown, so a caller that wants to stay quiet about
 *  a shutdown must not treat a missing handle as one — quietening it would hide a genuine
 *  start-up failure.
 *
 *  Note a shutdown does not produce it, which is why this narrowing costs nothing:
 *  `shutdownAsync` takes the write lock FIRST — `acquireWrite` sets the shutdown flag on its
 *  first line — and nulls the handles only after every in-flight reader has drained. So from
 *  the moment a shutdown starts, an arriving caller is refused by the recreation gate or by the
 *  read lock, and an in-flight one still holds a handle. The flag is permanent, so that stays
 *  true after the shutdown finishes as well.
 *
 *  The two reasons here both say a shutdown is already under way and cannot be anything
 *  else: `ShuttingDown` is the recreation gate reporting it, and `ReadLockRefused` can only
 *  happen after shutdown began, because the lock's shutdown flag is one-way and permanent.
 *
 *  Use `isDatabaseTornDown` to decide whether a read is worth trying again. Use this one to
 *  decide whether a refusal is our own sign-out doing its job. `sentry-error-policy.ts` is
 *  the caller that needs the narrow answer (df-0c444ca6bb2a). */
export function isDatabaseShuttingDown(err: unknown): boolean {
  return isDatabaseUnavailable(err) && SHUTDOWN_REASONS.includes(err.reason);
}

/** True when a database recovery cut the read short, and only then (#6209).
 *
 *  The narrowest of these tests: one reason, `Recovering`. The wrapper is alive and the same read
 *  will work once the recovery finishes. It is for a caller whose only loss is an optional display
 *  that its next reload rebuilds, such as the Home goal card's status or the loading spinner's
 *  hints. Such a caller skips, reports at the info tier, and leaves every other reason on its
 *  existing path. The other transient reasons are NOT included: a singleton that start-up has not
 *  loaded is a different condition, and those callers keep reporting it. */
export function isDatabaseRecovering(err: unknown): err is DatabaseUnavailableError {
  return isDatabaseUnavailable(err) && err.reason === DatabaseUnavailableReason.Recovering;
}

/** The reasons that mean a shutdown had already started. Kept as a list, beside
 *  `TRANSIENT_REASONS`, so that a reason added to the enum has one obvious place to be
 *  classified on each side rather than being forgotten inside a chain of `===`. */
const SHUTDOWN_REASONS: readonly DatabaseUnavailableReason[] = [
  DatabaseUnavailableReason.ShuttingDown,
  DatabaseUnavailableReason.ReadLockRefused,
];
